import { el, btn } from '@/ui/dom';
import type { City } from '@/config/cities';

/**
 * GlobeScreen.ts — the DOM layer over the tour globe flight (M9; redesigned
 * M65 to the owner's reference art): a gold WORLD TOUR CUP kicker between thin
 * rules up top, FROM / TO city blocks flanking the planet, and a bottom
 * progress rail — origin pin lit gold, destination pin waiting, one dot per
 * tour race with the current leg glowing. Motion-streak side washes come from
 * CSS; tap anywhere skips.
 */

const PIN_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 2a7 7 0 0 0-7 7c0 4.9 5.6 11.2 6.4 12.1a.8.8 0 0 0 1.2 0C13.4 20.2 19 13.9 19 9a7 7 0 0 0-7-7Zm0 9.6A2.6 2.6 0 1 1 12 6.4a2.6 2.6 0 0 1 0 5.2Z"/></svg>';

export class GlobeScreen {
  private root: HTMLDivElement;
  private raceLine: HTMLDivElement;
  private fromName: HTMLDivElement;
  private fromCountry: HTMLDivElement;
  private toName: HTMLDivElement;
  private toCountry: HTMLDivElement;
  private railFrom: HTMLSpanElement;
  private railTo: HTMLSpanElement;
  private railDots: HTMLDivElement;

  constructor(parent: HTMLElement, onSkip: () => void) {
    this.root = el('div', 'screen screen-globe', parent);
    this.root.style.display = 'none';

    // Top: the gold cup kicker between thin rules + the race counter.
    const top = el('div', 'globe-top', this.root);
    const kicker = el('div', 'globe-kicker', top);
    el('span', 'globe-kicker-rule', kicker);
    el('span', 'globe-kicker-text', kicker).textContent = 'WORLD TOUR CUP';
    el('span', 'globe-kicker-rule', kicker);
    this.raceLine = el('div', 'globe-raceline', top);

    // FROM (left) / TO (right) city blocks flanking the planet.
    const fromBlock = el('div', 'globe-block globe-block-from', this.root);
    el('div', 'globe-block-label', fromBlock).textContent = 'FROM';
    this.fromName = el('div', 'globe-block-city', fromBlock);
    this.fromCountry = el('div', 'globe-block-country', fromBlock);

    const toBlock = el('div', 'globe-block globe-block-to', this.root);
    el('div', 'globe-block-label globe-block-label-to', toBlock).textContent = 'TO';
    this.toName = el('div', 'globe-block-city', toBlock);
    this.toCountry = el('div', 'globe-block-country', toBlock);

    // Bottom: the progress rail (origin pin → race dots → destination pin).
    const rail = el('div', 'globe-rail', this.root);
    const fromEnd = el('div', 'globe-rail-end globe-rail-origin', rail);
    const fromPin = el('span', 'globe-rail-pin', fromEnd);
    fromPin.innerHTML = PIN_SVG;
    this.railFrom = el('span', 'globe-rail-name', fromEnd);
    this.railDots = el('div', 'globe-rail-track', rail);
    const toEnd = el('div', 'globe-rail-end globe-rail-dest', rail);
    const toPin = el('span', 'globe-rail-pin', toEnd);
    toPin.innerHTML = PIN_SVG;
    this.railTo = el('span', 'globe-rail-name', toEnd);

    btn('TAP TO SKIP', 'btn btn-ghost globe-skip', this.root, onSkip);
    this.root.addEventListener('click', onSkip);
  }

  show(from: City, to: City, raceNumber: number, total: number): void {
    this.raceLine.textContent = `RACE ${raceNumber} OF ${total}`;
    this.fromName.textContent = from.name.toUpperCase();
    this.fromCountry.textContent = from.country.toUpperCase();
    this.toName.textContent = to.name.toUpperCase();
    this.toCountry.textContent = to.country.toUpperCase();
    this.railFrom.textContent = from.name.toUpperCase();
    this.railTo.textContent = to.name.toUpperCase();

    // One dot per tour race; everything raced glows, the current leg pulses.
    this.railDots.replaceChildren();
    for (let i = 1; i <= total; i++) {
      const d = el('span', 'globe-rail-dot', this.railDots);
      if (i < raceNumber) d.classList.add('done');
      if (i === raceNumber) d.classList.add('now');
    }
    this.root.style.display = '';
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
